/**
 * Shared form schema — the single source of truth for validation,
 * used by BOTH the browser (per-step inline validation) and the
 * server proxy (authoritative validation before forwarding to
 * Power Automate).
 *
 * Keep this file free of DOM and Node APIs (no document, no process.env)
 * so it bundles cleanly into both targets.
 */
import { z } from 'zod'

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

/**
 * Every island the system knows. This is the SharePoint `Island` column's
 * option set (backfilled rows hold all of them) and must not shrink — the
 * donor form offers the subset in OFFERED_ISLANDS below.
 */
export const ISLANDS = ['Oahu', 'Maui', 'Big Island', 'Kauai', 'Molokai', 'Lanai', 'Other island'] as const

/**
 * Islands the DONOR FORM offers, in dropdown order. NKF (Marty, 2026-10-16):
 * "only Maui and Oahu are accepting cars for donations" — the rest are
 * commented out, not deleted, so re-enabling one is uncommenting its line
 * (the form, its validation and the smoke test all read this list). Staff
 * can still pick any ISLANDS value on the record form in SharePoint.
 */
export const OFFERED_ISLANDS = [
  'Oahu',
  'Maui',
  // 'Big Island',   — paused 2026-10-16
  // 'Kauai',        — paused 2026-10-16
  // 'Molokai',      — paused 2026-10-16
  // 'Lanai',        — paused 2026-10-16
  // 'Other island', — paused 2026-10-16 (its "Which island?" follow-up stays wired)
] as const satisfies readonly (typeof ISLANDS)[number][]

/**
 * All US states + DC. The form is currently Hawaii-only (the select is
 * locked to HI) but the program may expand, so the payload is bound to the
 * two-letter code from day one.
 */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: "Hawai'i" },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
] as const

const STATE_CODES = US_STATES.map((s) => s.code) as [string, ...string[]]

/**
 * Islands where Kidney Cars currently provides pickup service — the
 * `islandEligible` stamp the server adds for staff triage. Big Island paused
 * 2026-10-16 with the dropdown change above; put it back in both places.
 */
export const SERVED_ISLANDS = ['Oahu', 'Maui' /* , 'Big Island' — paused 2026-10-16 */] as const

export function isIslandEligible(island: string): boolean {
  return (SERVED_ISLANDS as readonly string[]).includes(island)
}

/**
 * States the form accepts. The state select is locked to HI in the browser;
 * this is the server-side half (a crafted POST with another state used to
 * skip the island checks). Add a code here when the program expands.
 */
export const OFFERED_STATES = ['HI'] as const satisfies readonly (typeof US_STATES)[number]['code'][]

/**
 * Islands where only vehicles that run AND drive are accepted (NKF, meeting
 * of 2026-10-20: Maui — towing there is limited to drivable cars). Oahu takes
 * any condition.
 */
export const DRIVABLE_ONLY_ISLANDS = ['Maui'] as const satisfies readonly (typeof ISLANDS)[number][]

/**
 * `runningCondition` values that count as drivable. Deliberately strict:
 * 'Unknown' and 'Runs but does not drive' are NOT drivable — open question
 * for NKF (README → To confirm with the client); widen this list if they
 * prefer to allow 'Unknown' and call the donor.
 */
export const DRIVABLE_CONDITIONS = ['Runs and drives'] as const

export function requiresDrivable(island: string): boolean {
  return (DRIVABLE_ONLY_ISLANDS as readonly string[]).includes(island)
}

export function isDrivable(runningCondition: string): boolean {
  return (DRIVABLE_CONDITIONS as readonly string[]).includes(runningCondition)
}

// Codes follow the client's existing (NCIC-style) values. The makes after
// SUZI were added for coverage of common modern donations (Tesla etc.) —
// confirm the new codes against the SharePoint mapping before launch.
export const VEHICLE_MAKES = [
  'ACUR', 'AUDI', 'BMW', 'BUIC', 'CADI', 'CHEV', 'CHRY', 'DODG', 'FORD', 'GMC',
  'HOND', 'HYUN', 'INFI', 'ISU', 'JAGU', 'JEEP', 'KIA', 'LEXS', 'LINC', 'MAZD',
  'MERC', 'MERZ', 'MITS', 'NISS', 'OLDS', 'PLYM', 'PONT', 'SAA', 'STRN', 'SUBA',
  'SUZI', 'TOYT', 'VOLK', 'VOLV',
  'TESL', 'RAM', 'SCIO', 'MINI', 'PORS', 'LNDR', 'FIAT', 'GENE',
  'OTHER',
] as const

/** Sentinel `model` value when the donor's model isn't in the vPIC list. */
export const MODEL_NOT_LISTED = '__OTHER__'

/**
 * Models NKF cannot accept on any island (NKF, 2026-10-20: the Nissan Leaf —
 * no auction market for its battery). `make` is the VEHICLE_MAKES code,
 * `makeName` the label vPIC and donors use. The model dropdown hides these
 * and `crossFieldIssues` rejects them wherever the name shows up — the
 * dropdown, the "not listed" free text, or a VIN decode.
 */
export const EXCLUDED_MODELS = [{ make: 'NISS', makeName: 'Nissan', model: 'Leaf' }] as const

/**
 * True when `model` names an excluded model under `make`. Case-insensitive
 * and tolerant of trim levels ("LEAF", "Leaf SV", "Nissan Leaf"); under make
 * OTHER only the "<makeName> <model>" spelling counts, so a different make's
 * "Leaf" (if one ever exists) is not caught by accident.
 */
export function isModelExcluded(make: string, model: string): boolean {
  const m = model.trim().replace(/\s+/g, ' ')
  if (m === '') return false
  return EXCLUDED_MODELS.some((e) => {
    const withMake = new RegExp(`^${e.makeName}\\s+${e.model}\\b`, 'i')
    const bare = new RegExp(`^${e.model}\\b`, 'i')
    if (make === e.make) return bare.test(m) || withMake.test(m)
    return make === 'OTHER' && withMake.test(m)
  })
}

// Donor-facing messages for the eligibility rules — exported so the browser
// notices, the field errors and the server's 400 all say the same thing.
export const MSG_STATE_HI_ONLY = 'Kidney Cars currently accepts vehicle donations in Hawai\u02bbi only.'
export const MSG_LEAF_EXCLUDED = 'We are not able to accept Nissan Leaf vehicles at this time.'
export const MSG_MAUI_DRIVABLE = 'On Maui, Kidney Cars can only accept vehicles that run and drive.'
export const MSG_MAUI_DRIVABLE_ISLAND =
  'Kidney Cars can only accept vehicles that run and drive on Maui \u2014 please update the vehicle condition or choose a different island.'

/**
 * Vehicle condition rating — the NAAA Vehicle Condition Grading Scale.
 * The select stores the numeric grade as a string ('0' worst … '5' best) but
 * shows text labels (see index.html); the schema validates the grade and
 * transforms it to a real number so SharePoint's `CarCondition` Number column
 * receives a number, not text.
 */
export const CAR_CONDITION_VALUES = ['5', '4', '3', '2', '1', '0'] as const

// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------

const requiredString = (label: string, max = 100) =>
  z
    .string(`${label} is required.`)
    .trim()
    .min(1, `${label} is required.`)
    .max(max, `${label} is too long.`)

const optionalString = (max = 200) => z.string().trim().max(max, 'This entry is too long.').default('')

// ---------------------------------------------------------------------------
// Phone parsing / formatting
// ---------------------------------------------------------------------------

export interface ParsedPhone {
  /** Exactly 10 national digits. */
  digits: string
  /** Extension digits ('' when none), max 6. */
  ext: string
}

/**
 * Accepts anything a human might enter — "(808) 555-1234 x89", "8085551234",
 * "1-808-555-1234 ext 89" — and returns the structured number, or null when
 * it isn't a valid 10-digit US number (+ optional extension).
 */
export function parsePhone(value: string): ParsedPhone | null {
  const lower = value.trim().toLowerCase().replace(/extension|ext\.?/g, 'x')
  const [numberPart = '', ...extParts] = lower.split(/[x#]/)
  let digits = numberPart.replace(/\D/g, '')
  const ext = extParts.join('').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1) // tolerate leading country code
  if (digits.length !== 10) return null
  if (ext.length > 6) return null
  return { digits, ext }
}

/**
 * Phone format forwarded to Power Automate / stored in SharePoint.
 *
 * >>> The SharePoint column format is NOT confirmed yet — when the client
 * >>> provides it, change ONLY this function (e.g. E.164 `+18085551234;ext=89`
 * >>> or bare digits `8085551234`). Everything else flows from here.
 */
export function formatPhoneForStorage(phone: ParsedPhone): string {
  return formatPhoneDisplay(phone)
}

/** Human-readable mask format: (808) 555-1234 x89 */
export function formatPhoneDisplay({ digits, ext }: ParsedPhone): string {
  const base = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  return ext ? `${base} x${ext}` : base
}

const PHONE_ERROR = 'Please enter a valid 10-digit phone number, e.g. (808) 555-1234.'

const requiredPhone = (label: string) =>
  z
    .string(`${label} is required.`)
    .trim()
    .min(1, `${label} is required.`)
    .max(30, `${label} is too long.`)
    .refine((v) => parsePhone(v) !== null, PHONE_ERROR)
    .transform((v) => formatPhoneForStorage(parsePhone(v)!))

const optionalPhone = () =>
  optionalString(30)
    .refine((v) => v === '' || parsePhone(v) !== null, PHONE_ERROR)
    .transform((v) => (v === '' ? '' : formatPhoneForStorage(parsePhone(v)!)))

// VINs never contain I, O, or Q. Accept partial VINs (6+) — donors often
// only have the last six digits handy; staff confirm the full VIN by phone.
const VIN_PATTERN = /^[A-HJ-NPR-Za-hj-npr-z0-9]{6,17}$/

// ---------------------------------------------------------------------------
// Form fields (everything the donor types — no meta, no anti-abuse fields)
// ---------------------------------------------------------------------------

const formFields = z.object({
  // Step 1 — Donor information
  title: optionalString(20),
  firstName: requiredString('First name'),
  lastName: requiredString('Last name'),
  email: z.email('Please enter a valid email address.').max(254, 'Email address is too long.'),
  phone: requiredPhone('Phone number'),
  alternatePhone: optionalPhone(),
  address: requiredString('Street address', 200),
  address2: optionalString(100),
  city: requiredString('City'),
  state: z.enum(STATE_CODES, 'Please select a state.'),
  zip: z.string('ZIP code is required.').trim().regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code.'),
  // Required only for Hawai'i — enforced in crossFieldChecks (the field is
  // hidden for other states).
  island: optionalString(20),
  islandOther: optionalString(100),
  bestTimeToCall: optionalString(20),
  hearAboutUs: optionalString(40),
  hearAboutUsOther: optionalString(200),
  additionalComments: optionalString(500),

  // Step 2 — Vehicle information
  vehicleType: z.enum(['Car', 'Truck', 'SUV', 'Van', 'Other'], 'Please select a vehicle type.'),
  vehicleTypeOther: optionalString(100),
  year: z
    .string('Please select the vehicle year.')
    .trim()
    .regex(/^\d{4}$/, 'Please select the vehicle year.')
    .refine((v) => {
      const year = Number(v)
      return year >= 1900 && year <= new Date().getFullYear() + 1
    }, 'Please select a valid vehicle year.'),
  make: z.enum(VEHICLE_MAKES, 'Please select the vehicle make.'),
  model: requiredString('Model', 80),
  modelOther: optionalString(80),
  vin: optionalString(17).refine(
    (v) => v === '' || VIN_PATTERN.test(v),
    'Please enter a valid VIN (the letters I, O, and Q are never used in a VIN).',
  ),
  mileage: optionalString(12)
    .transform((v) => v.replace(/[,\s]/g, ''))
    .refine((v) => v === '' || /^\d{1,7}$/.test(v), 'Please enter mileage as a number.'),
  runningCondition: z.enum(
    ['Runs and drives', 'Runs but does not drive', 'Does not run', 'Unknown'],
    'Please select the vehicle condition.',
  ),
  titleStatus: z.enum(
    ['Signed title in hand', 'Title available but not signed', 'Lost title', 'Lien on title', 'Unsure'],
    'Please select the title status.',
  ),
  namesOnTitle: requiredString('Name(s) on title', 150),
  hasKeys: z.enum(['Yes', 'No'], 'Please tell us if keys are available.'),
  currentRegistration: z.enum(['Yes', 'No', 'Unsure'], 'Please select an option for registration.'),
  currentSafety: z.enum(['Yes', 'No', 'Unsure'], 'Please select an option for the safety check.'),
  licensePlate: optionalString(10),
  // NAAA condition grade — select stores '0'–'5', stored as a number (0–5).
  carCondition: z
    .enum(CAR_CONDITION_VALUES, 'Please rate the condition of the vehicle from 0 to 5.')
    .transform((v) => Number(v)),
  // Required: notes are relied on during the first donor conversation/follow-up.
  vehicleNotes: requiredString('Vehicle notes', 1000),

  // Step 3 — Location & pickup
  preferredPickupDate: optionalString(10).refine(
    (v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v),
    'Please enter a valid date.',
  ),
  pickupAccess: optionalString(40),
  sameAsDonorAddress: z.boolean().default(true),
  pickupAddress: optionalString(250),
  canVehicleBeMoved: optionalString(10),
  preferredContactMethod: z.enum(['Phone', 'Email', 'Text message'], 'Please select a contact method.'),
  pickupNotes: optionalString(1000),

  // Step 4 — Consent
  consent: z.boolean('Please confirm and authorize contact before submitting.').refine(
    (v) => v === true,
    'Please confirm and authorize contact before submitting.',
  ),
})

type FormFieldShape = z.infer<typeof formFields>

export interface CrossFieldIssue {
  path: keyof FormFieldShape
  message: string
}

/**
 * Cross-field conditional requirements (mirrors the conditional UI).
 *
 * Accepts RAW form values (untrimmed strings / booleans), not just parsed
 * output: Zod skips object-level refinements whenever any base field fails
 * to parse — the norm while a donor is mid-form — so the browser calls this
 * directly to keep conditional requireds honest during per-field validation.
 */
export function crossFieldIssues(data: Record<string, unknown>): CrossFieldIssue[] {
  const str = (v: unknown) => String(v ?? '').trim()
  const issues: CrossFieldIssue[] = []
  // Hawai'i-only: closes the "another state skips the island checks" bypass.
  if (!(OFFERED_STATES as readonly string[]).includes(str(data.state))) {
    issues.push({ path: 'state', message: MSG_STATE_HI_ONLY })
  }
  if (data.state === 'HI') {
    if (!(OFFERED_ISLANDS as readonly string[]).includes(str(data.island))) {
      issues.push({ path: 'island', message: 'Please select your island.' })
    } else if (data.island === 'Other island' && str(data.islandOther) === '') {
      issues.push({ path: 'islandOther', message: 'Please tell us which island the vehicle is on.' })
    }
  }
  if (data.vehicleType === 'Other' && str(data.vehicleTypeOther) === '') {
    issues.push({ path: 'vehicleTypeOther', message: 'Please describe the type of vehicle.' })
  }
  if (data.model === MODEL_NOT_LISTED && str(data.modelOther) === '') {
    issues.push({ path: 'modelOther', message: 'Please enter the vehicle model.' })
  }
  // Maui: drivable only. Attached to BOTH fields — each step validates only
  // its own STEP_FIELDS, so step 2 catches a condition change and step 1
  // catches an island change made from the Review step's Edit link. The
  // condition must be answered first (empty on the first pass through step 1).
  const runningCondition = str(data.runningCondition)
  if (requiresDrivable(str(data.island)) && runningCondition !== '' && !isDrivable(runningCondition)) {
    issues.push({ path: 'runningCondition', message: MSG_MAUI_DRIVABLE })
    issues.push({ path: 'island', message: MSG_MAUI_DRIVABLE_ISLAND })
  }
  // Nissan Leaf: the dropdown value, or the "not listed" free text.
  const effectiveModel = data.model === MODEL_NOT_LISTED ? str(data.modelOther) : str(data.model)
  if (isModelExcluded(str(data.make), effectiveModel)) {
    issues.push({ path: data.model === MODEL_NOT_LISTED ? 'modelOther' : 'model', message: MSG_LEAF_EXCLUDED })
  }
  if (!data.sameAsDonorAddress && str(data.pickupAddress) === '') {
    issues.push({ path: 'pickupAddress', message: 'Please provide the vehicle location.' })
  }
  return issues
}

/** Zod adapter for crossFieldIssues, attached via superRefine. */
function crossFieldChecks(data: FormFieldShape, ctx: z.RefinementCtx): void {
  for (const { path, message } of crossFieldIssues(data)) {
    ctx.addIssue({ code: 'custom', path: [path], message })
  }
}

/** Donor-entered fields only — what the browser validates per step. */
export const formSchema = formFields.superRefine(crossFieldChecks)

export type FormValues = z.infer<typeof formSchema>

/**
 * Full request body the browser sends to POST /api/submit.
 * The extra fields are consumed by the proxy and never forwarded.
 */
export const clientPayloadSchema = formFields
  .extend({
    turnstileToken: z.string().max(4096).default(''),
    contact_website: z.string().max(500).default(''), // honeypot — must be empty
    formLoadedAt: z.number().int().nonnegative().optional(),
  })
  .superRefine(crossFieldChecks)

export type ClientPayload = z.infer<typeof clientPayloadSchema>

// ---------------------------------------------------------------------------
// Step + label metadata (drives per-step validation and the review summary)
// ---------------------------------------------------------------------------

export const STEP_FIELDS: readonly (readonly (keyof FormValues)[])[] = [
  [
    'title', 'firstName', 'lastName', 'email', 'phone', 'alternatePhone',
    'address', 'address2', 'city', 'state', 'zip', 'island', 'islandOther',
    'bestTimeToCall', 'hearAboutUs', 'hearAboutUsOther', 'additionalComments',
  ],
  [
    'vehicleType', 'vehicleTypeOther', 'year', 'make', 'model', 'modelOther', 'vin', 'mileage',
    'runningCondition', 'titleStatus', 'namesOnTitle', 'hasKeys',
    'currentRegistration', 'currentSafety', 'licensePlate', 'carCondition', 'vehicleNotes',
  ],
  [
    'preferredPickupDate', 'pickupAccess', 'sameAsDonorAddress', 'pickupAddress',
    'canVehicleBeMoved', 'preferredContactMethod', 'pickupNotes',
  ],
  ['consent'],
]

export const FIELD_LABELS: Record<keyof FormValues, string> = {
  title: 'Title',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email address',
  phone: 'Phone number',
  alternatePhone: 'Alternate phone',
  address: 'Street address',
  address2: 'Address line 2',
  city: 'City',
  state: 'State',
  zip: 'ZIP code',
  island: 'Island',
  islandOther: 'Which island',
  bestTimeToCall: 'Best time to call',
  hearAboutUs: 'How did you hear about Kidney Cars',
  hearAboutUsOther: 'How you heard about us',
  additionalComments: 'Additional comments',
  vehicleType: 'Vehicle type',
  vehicleTypeOther: 'Vehicle type (other)',
  year: 'Year',
  make: 'Make',
  model: 'Model',
  modelOther: 'Model',
  vin: 'VIN',
  mileage: 'Mileage',
  runningCondition: 'Condition',
  titleStatus: 'Certificate of title',
  namesOnTitle: 'Name(s) on title',
  hasKeys: 'Keys available',
  currentRegistration: 'Current registration',
  currentSafety: 'Current safety check',
  licensePlate: 'License plate',
  carCondition: 'Condition rating',
  vehicleNotes: 'Vehicle notes',
  preferredPickupDate: 'Preferred pickup date',
  pickupAccess: 'Accessibility',
  sameAsDonorAddress: 'Vehicle at donor address',
  pickupAddress: 'Vehicle location',
  canVehicleBeMoved: 'Can the vehicle be moved',
  preferredContactMethod: 'Preferred contact method',
  pickupNotes: 'Pickup notes',
  consent: 'Consent to contact',
}
